import React from "react";
import { isCombining, keyCapLabel } from "./arabicText";

/**
 * The Arabic 101 keyboard, drawn from the layout the API serves.
 *
 * Deliberately dir="ltr". The characters on it are Arabic, but the thing being
 * drawn is a physical keyboard: Q is on the left of a real board no matter what
 * script it prints. Letting this inherit RTL would mirror the rows and teach
 * every key position backwards.
 */

// Finger -> accent token. See DESIGN.md §7a.
const FINGER_COLOR = {
  "l-pinky": "var(--tag-rose)",
  "r-pinky": "var(--tag-rose)",
  "l-ring": "var(--tag-violet)",
  "r-ring": "var(--tag-violet)",
  "l-middle": "var(--tag-teal)",
  "r-middle": "var(--tag-teal)",
  "l-index": "var(--gold)",
  "r-index": "var(--gold)",
  thumb: "var(--slate)",
};

const FINGER_LABEL = {
  "l-pinky": "left little finger",
  "l-ring": "left ring finger",
  "l-middle": "left middle finger",
  "l-index": "left index finger",
  "r-index": "right index finger",
  "r-middle": "right middle finger",
  "r-ring": "right ring finger",
  "r-pinky": "right little finger",
  thumb: "either thumb",
};

export function fingerLabel(finger) {
  return FINGER_LABEL[finger] || "";
}

// Each row is nudged right by roughly the stagger of a real keyboard.
const ROW_INDENT = { number: 0, top: 0.6, home: 0.95, bottom: 1.6 };

function KeyCap({ code, spec, isNext, wasWrong, shiftPending }) {
  const base = spec.base || "";
  const shift = spec.shift || "";
  // Only show the shift legend when it is Arabic — the Latin punctuation on the
  // shift layer is noise for a learner and crowds the cap.
  const showShift = shift && /[؀-ۿ]/.test(shift);
  const accent = FINGER_COLOR[spec.finger] || "var(--gold-dim)";

  const style = isNext
    ? {
        background: "var(--gold-bg)",
        borderColor: "var(--gold)",
        boxShadow: "0 0 0 3px rgba(201,162,39,0.22)",
        color: "var(--gold-bright)",
      }
    : wasWrong
    ? { background: "rgba(220,90,90,0.15)", borderColor: "#dc5a5a", color: "#e58c8c" }
    : { background: "var(--ink)", borderColor: "var(--panel-line)", color: "var(--parchment)" };

  return (
    <div
      className="relative flex flex-col items-center justify-center rounded-md border shrink-0 transition-all"
      style={{ width: "2.35rem", height: "2.35rem", ...style }}
      title={`${code} — ${fingerLabel(spec.finger)}`}
      aria-hidden="true"
    >
      {showShift && (
        <span
          className="absolute top-[2px] right-[3px] font-arabic leading-none"
          style={{
            fontSize: "0.62rem",
            color: isNext && shiftPending ? "var(--gold-bright)" : "var(--parchment-dim)",
            opacity: isNext && shiftPending ? 1 : 0.75,
          }}
        >
          {keyCapLabel(shift)}
        </span>
      )}
      <span
        className="font-arabic leading-none"
        style={{ fontSize: isCombining(base) ? "0.95rem" : "1.05rem", marginTop: showShift ? "0.3rem" : 0 }}
      >
        {keyCapLabel(base)}
      </span>
      {/* finger tint along the bottom edge */}
      <span
        className="absolute bottom-0 left-1/2 -translate-x-1/2 rounded-full"
        style={{ width: "60%", height: 2, background: accent, opacity: isNext ? 1 : 0.45 }}
      />
    </div>
  );
}

export default function KeyboardDiagram({ layout, nextKey, wrongKey }) {
  if (!layout?.keys || !layout?.row_order) return null;

  const rows = ["number", "top", "home", "bottom"];

  return (
    <div className="w-full overflow-x-auto scroll-elegant" dir="ltr">
      <div className="inline-flex flex-col gap-1 min-w-min mx-auto p-1">
        {rows.map((row) => (
          <div
            key={row}
            className="flex gap-1"
            style={{ paddingLeft: `${(ROW_INDENT[row] || 0) * 2.35}rem` }}
          >
            {(layout.row_order[row] || []).map((code) => (
              <KeyCap
                key={code}
                code={code}
                spec={layout.keys[code]}
                isNext={nextKey?.code === code}
                wasWrong={wrongKey?.code === code}
                shiftPending={!!nextKey?.shift}
              />
            ))}
          </div>
        ))}
        {/* space bar */}
        <div className="flex gap-1 justify-center mt-0.5">
          <div
            className="rounded-md border"
            style={{
              width: "14rem",
              height: "1.5rem",
              background: nextKey?.code === "Space" ? "var(--gold-bg)" : "var(--ink)",
              borderColor: nextKey?.code === "Space" ? "var(--gold)" : "var(--panel-line)",
              boxShadow: nextKey?.code === "Space" ? "0 0 0 3px rgba(201,162,39,0.22)" : "none",
            }}
            aria-hidden="true"
          />
        </div>
      </div>
    </div>
  );
}
